<template>
    <div class="survey-fill-page">
        <b-button variant="link" class="back-link mb-3 px-0" @click="$router.push('/customer-portal/rsvp/' + invitationId)">
            <i class="fa fa-arrow-left mr-1"></i> Back to Invitation
        </b-button>

        <div v-if="loading" class="text-center py-5">
            <b-spinner variant="primary" label="Loading..." />
            <p class="mt-2 text-muted">Loading survey...</p>
        </div>

        <div v-else-if="error">
            <b-alert show variant="danger">
                <i class="fa fa-exclamation-triangle mr-1"></i>
                {{ error }}
            </b-alert>
        </div>

        <div v-else-if="submitted || hasExistingResponse" class="success-state">
            <b-card class="shadow-sm border-0 text-center py-4">
                <div class="success-icon mb-3">
                    <i class="fa fa-check"></i>
                </div>
                <h4 class="text-success mb-2">Response Submitted</h4>
                <p class="text-muted mb-4">Thank you for completing this survey.</p>

                <div v-if="displayAnswers.length > 0" class="text-left mt-4">
                    <h6 class="font-w700 mb-3">Your Answers</h6>
                    <div v-for="(item, idx) in displayAnswers" :key="idx" class="answer-display-row mb-3">
                        <div class="font-w600 text-dark mb-1">{{ item.question_text }}</div>
                        <div class="text-muted">
                            <template v-if="item.question_type === 5">
                                <span v-for="star in 5" :key="star">
                                    <i :class="star <= parseInt(item.answer) ? 'fa fa-star text-warning' : 'fa fa-star-o text-muted'"></i>
                                </span>
                            </template>
                            <template v-else-if="Array.isArray(item.answer)">
                                {{ item.answer.join(', ') }}
                            </template>
                            <template v-else>
                                {{ item.answer || '-' }}
                            </template>
                        </div>
                    </div>
                </div>
            </b-card>
        </div>

        <div v-else-if="survey">
            <b-card class="shadow-sm border-0 mb-4">
                <h4 class="font-w700 mb-1">{{ survey.title }}</h4>
                <p v-if="survey.description" class="text-muted mb-0">{{ survey.description }}</p>
            </b-card>

            <b-form @submit.prevent="submitSurvey">
                <b-card
                    v-for="(question, qIndex) in survey.questions"
                    :key="question.id"
                    class="shadow-sm border-0 mb-3 question-card"
                >
                    <div class="question-label mb-2">
                        <span class="font-w600">{{ qIndex + 1 }}. {{ question.question_text }}</span>
                        <span v-if="question.is_required" class="text-danger ml-1">*</span>
                    </div>

                    <div v-if="question.question_type === 1">
                        <b-form-input
                            v-model="answers[question.id]"
                            placeholder="Your answer"
                            class="form-control-modern"
                        ></b-form-input>
                    </div>

                    <div v-else-if="question.question_type === 2">
                        <b-form-textarea
                            v-model="answers[question.id]"
                            placeholder="Your answer"
                            rows="3"
                            class="form-control-modern"
                        ></b-form-textarea>
                    </div>

                    <div v-else-if="question.question_type === 3">
                        <div v-for="(opt, oIdx) in question.options" :key="oIdx" class="mb-2">
                            <b-form-radio
                                v-model="answers[question.id]"
                                :value="opt"
                                :name="'q_' + question.id"
                            >
                                {{ opt }}
                            </b-form-radio>
                        </div>
                    </div>

                    <div v-else-if="question.question_type === 4">
                        <div v-for="(opt, oIdx) in question.options" :key="oIdx" class="mb-2">
                            <b-form-checkbox
                                :checked="(answers[question.id] || []).includes(opt)"
                                @change="toggleCheckbox(question.id, opt, $event)"
                            >
                                {{ opt }}
                            </b-form-checkbox>
                        </div>
                    </div>

                    <div v-else-if="question.question_type === 5" class="star-rating">
                        <span
                            v-for="star in 5"
                            :key="star"
                            class="star-btn"
                            :class="{ 'star-active': star <= (answers[question.id] || 0) }"
                            @click="setRating(question.id, star)"
                        >
                            <i :class="star <= (answers[question.id] || 0) ? 'fa fa-star' : 'fa fa-star-o'"></i>
                        </span>
                        <span v-if="answers[question.id]" class="ml-2 text-muted small">{{ answers[question.id] }}/5</span>
                    </div>

                    <div v-else-if="question.question_type === 6">
                        <b-form-select
                            v-model="answers[question.id]"
                            :options="[{ value: '', text: 'Select an option' }, ...question.options.map(o => ({ value: o, text: o }))]"
                            class="form-control-modern"
                        ></b-form-select>
                    </div>
                </b-card>

                <div class="text-center mt-4">
                    <b-button variant="primary" type="submit" size="lg" :disabled="submitting" class="px-5 submit-btn">
                        <b-spinner v-if="submitting" small class="mr-1" />
                        Submit Survey
                    </b-button>
                </div>
            </b-form>
        </div>
    </div>
</template>

<script>
export default {
    name: 'SurveyFillForm',
    data() {
        return {
            loading: true,
            error: null,
            survey: null,
            answers: {},
            submitting: false,
            submitted: false,
            existingResponse: null,
        }
    },
    computed: {
        invitationId() {
            return this.$route.params.invitationId
        },
        surveyId() {
            return this.$route.params.surveyId
        },
        hasExistingResponse() {
            return !!this.existingResponse
        },
        displayAnswers() {
            if (!this.existingResponse || !this.existingResponse.answers) return []
            return this.existingResponse.answers
        },
    },
    mounted() {
        this.loadSurvey()
    },
    methods: {
        async loadSurvey() {
            this.loading = true
            this.error = null
            try {
                const data = await this.$store.dispatch('eventInvitation/fetchMySurveyDetail', {
                    invitationId: this.invitationId,
                    surveyId: this.surveyId,
                })
                this.survey = data.survey || data
                if (data.existing_response) {
                    this.existingResponse = data.existing_response
                }
                if (data.prefilled_values && !this.hasExistingResponse) {
                    this.applyPrefill(data.prefilled_values)
                }
            } catch (err) {
                this.error = err?.response?.data?.error?.message || 'Failed to load survey.'
            } finally {
                this.loading = false
            }
        },
        applyPrefill(prefilled) {
            for (const [questionId, value] of Object.entries(prefilled)) {
                if (value !== null && value !== '') {
                    this.$set(this.answers, parseInt(questionId), value)
                }
            }
        },
        toggleCheckbox(questionId, option, checked) {
            if (!this.answers[questionId]) {
                this.$set(this.answers, questionId, [])
            }
            const arr = this.answers[questionId]
            if (checked) {
                if (!arr.includes(option)) arr.push(option)
            } else {
                const idx = arr.indexOf(option)
                if (idx !== -1) arr.splice(idx, 1)
            }
        },
        setRating(questionId, value) {
            this.$set(this.answers, questionId, value)
        },
        async submitSurvey() {
            if (!this.survey || !this.survey.questions) return

            const missing = this.survey.questions.filter(q => {
                if (!q.is_required) return false
                const val = this.answers[q.id]
                if (val === undefined || val === null || val === '') return true
                if (Array.isArray(val) && val.length === 0) return true
                return false
            })

            if (missing.length > 0) {
                this.$bvToast.toast(`Please answer all required questions. Missing: ${missing.map(q => q.question_text).join(', ')}`, {
                    title: 'Required Fields',
                    variant: 'warning',
                    solid: true,
                })
                return
            }

            const formattedAnswers = this.survey.questions.map(q => ({
                question_id: q.id,
                answer: this.answers[q.id] || null,
            }))

            this.submitting = true
            try {
                await this.$store.dispatch('eventInvitation/submitMySurvey', {
                    invitationId: this.invitationId,
                    surveyId: this.surveyId,
                    answers: formattedAnswers,
                })
                this.submitted = true
                this.$bvToast.toast('Survey submitted successfully.', {
                    title: 'Success',
                    variant: 'success',
                    solid: true,
                })
            } catch (err) {
                this.$bvToast.toast(err?.response?.data?.error?.message || 'Failed to submit survey.', {
                    title: 'Error',
                    variant: 'danger',
                    solid: true,
                })
            } finally {
                this.submitting = false
            }
        },
    },
}
</script>

<style scoped>
.back-link {
    font-weight: 600;
    font-size: 0.9rem;
    text-decoration: none;
}

.form-control-modern {
    border: 1px solid #e9ecef;
    border-radius: 8px;
}

.form-control-modern:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.question-card {
    border-radius: 10px;
}

.question-label {
    font-size: 0.95rem;
}

.star-rating {
    display: flex;
    align-items: center;
}

.star-btn {
    font-size: 1.6rem;
    cursor: pointer;
    color: #dee2e6;
    transition: color 0.15s ease, transform 0.15s ease;
    margin-right: 4px;
}

.star-btn:hover {
    transform: scale(1.15);
}

.star-btn.star-active {
    color: #f0ad4e;
}

.star-btn.star-active i,
.star-btn:hover i {
    color: #f0ad4e;
}

.submit-btn {
    border-radius: 8px;
    font-weight: 600;
}

.success-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: linear-gradient(135deg, #27ae60, #2ecc71);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    margin: 0 auto;
}

.answer-display-row {
    padding-bottom: 12px;
    border-bottom: 1px solid #f0f0f0;
}

.answer-display-row:last-child {
    border-bottom: none;
}
</style>
